import type { Meta, StoryFn } from '@storybook/react';
import { useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { action } from '@storybook/addon-actions';

import { HTML, Card, menuHelpers } from '@pega/cosmos-react-core';
import type { OmitStrict, MenuItemProps, MenuProps } from '@pega/cosmos-react-core';
import { ArticleBuddy } from '@pega/cosmos-react-work';
import type {
  ArticleBuddyProps,
  Reaction,
  BuddyResponseProps,
  ArticleFeedbackProps,
  ArticleBuddyListProps
} from '@pega/cosmos-react-work';

import { ArticleBuddyMockData } from './ArticleList.mocks';

export default {
  title: 'Work/ArticleList',
  component: ArticleBuddy
} as Meta;

export interface ArticleBuddyDemoProps {
  showDisclaimer: boolean;
  showReferences: boolean;
  showActions: boolean;
  showError: boolean;
  showFeedback: boolean;
}

type MockBuddyArticles = OmitStrict<ArticleBuddyProps, 'query' | 'onSubmit' | 'onClear'>;

export const ArticleBuddyDemo: StoryFn<ArticleBuddyDemoProps> = (args: ArticleBuddyDemoProps) => {
  const [searchVal, setSearchVal] = useState('');
  const [commentVal, setCommentVal] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchResult, setSearchResult] = useState<MockBuddyArticles['response']>();
  const [reaction, setReaction] = useState<Reaction>(undefined);
  const [showThankYouMessage, setShowThankYouMessage] = useState(false);
  const [items, setItems] = useState<MenuProps['items']>(() =>
    Array.from({ length: 10 }, (_, i) => {
      return {
        id: `${i + 1}`,
        primary: `Buddy ${i + 1}`,
        selected: false
      };
    })
  );
  const [buddyInfo, setBuddyInfo] = useState('');

  /** updates buddy response */
  const updateBuddyArticles = (mockBuddyArticle: MockBuddyArticles['response']) => {
    const actionItems = {
      items: [
        { id: '1', text: 'Share' },
        { id: '2', text: 'Copy' }
      ]
    };

    return {
      title: mockBuddyArticle?.title,
      content: mockBuddyArticle?.content,
      references: mockBuddyArticle?.references,
      disclaimer: mockBuddyArticle?.disclaimer,
      actions: actionItems
    };
  };

  /** async call the response */
  const fetchSearchResult: () => Promise<{
    data: BuddyResponseProps;
  }> = () => {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          data: updateBuddyArticles(ArticleBuddyMockData)
        });
      }, 1000);
    });
  };

  /** fetches the response and gets the sets the response */
  const getResults = async () => {
    setLoading(true);
    const res = await fetchSearchResult();
    setSearchResult(res.data);
    setLoading(false);
  };

  const queryParams: ArticleBuddyProps['query'] = {
    label: 'Ask buddy',
    placeholder: 'Ask your question here...',
    value: searchVal,
    onChange: (e: ChangeEvent<HTMLTextAreaElement>) => setSearchVal(e.target.value),
    labelHidden: true
  };

  const commentParams: ArticleFeedbackProps['comment'] = {
    placeholder: 'How can I improve...',
    value: commentVal,
    onChange: (e: ChangeEvent<HTMLTextAreaElement>) => setCommentVal(e.target.value),
    labelHidden: false
  };

  const onReaction = (feedback: Reaction) => {
    setReaction(feedback);
  };

  const feedbackProps = {
    question: 'Was this helpful?',
    reaction,
    onReaction,
    comment: commentParams,
    onSubmit: (response: { reaction: Reaction; comment: string }) => {
      action('onClick')(`Response sent ${response.reaction} ${response.comment}`);
      setCommentVal('');
      setReaction(undefined);
      setShowThankYouMessage(true);
    },
    showThankYouMessage
  };

  const handleMenuItemClick = (id: MenuItemProps['id']) => {
    setItems(cur => menuHelpers.toggleSelected(cur, id, 'single-select'));
    setBuddyInfo(`Buddy ${id}`);
  };

  const itemsToRender = useMemo(() => {
    return menuHelpers.mapTree(items, item => {
      return {
        ...item,
        visual: undefined,
        secondary: undefined
      };
    });
  }, [items]);

  const buttonText = useMemo(() => {
    const selected = menuHelpers.getSelected(items)[0];
    return selected ? selected.primary : 'Select buddy';
  }, [items]);

  const buddyListProps: ArticleBuddyListProps = {
    text: buttonText,
    variant: 'text',
    menu: {
      items: itemsToRender,
      onItemClick: handleMenuItemClick
    },
    contextualLabel: buttonText === 'Select buddy' ? buttonText : `Selected buddy: ${buttonText}`
  };

  const onClickRelatedQuestions = (item: { id: string; name: string }) => {
    setSearchVal(item.name);
    getResults();
  };

  const relatedQuestions = {
    title: 'Related questions',
    items: [
      { id: '1', name: 'How to file complaint?', onClick: onClickRelatedQuestions },
      {
        id: '2',
        name: 'How to view profile?',
        onClick: onClickRelatedQuestions
      },
      {
        id: '3',
        name: 'How to subscribe bill payment every month?',
        onClick: onClickRelatedQuestions
      }
    ]
  };
  return (
    <Card>
      <ArticleBuddy
        query={queryParams}
        onSubmit={getResults}
        buddyOptions={{
          list: buddyListProps,
          ...(buttonText !== 'Select buddy' && {
            buddyInfo: {
              title: buddyInfo,
              description:
                'This buddy will provide assistance for customer service related queries.'
            }
          })
        }}
        onClear={() => {
          setSearchVal('');
        }}
        loading={loading}
        response={{
          title: searchResult?.title,
          actions: args.showActions ? searchResult?.actions : undefined,
          content:
            !args.showError && searchResult?.content ? (
              <HTML content={ArticleBuddyMockData.content} />
            ) : undefined,
          disclaimer: args.showDisclaimer ? ArticleBuddyMockData?.disclaimer : undefined,
          references: args.showReferences ? searchResult?.references : undefined,
          feedback: args.showFeedback ? feedbackProps : undefined,
          error: args.showError,
          relatedQuestions: searchResult ? relatedQuestions : undefined
        }}
      />
    </Card>
  );
};

ArticleBuddyDemo.args = {
  showDisclaimer: true,
  showActions: true,
  showReferences: true,
  showError: false,
  showFeedback: true
};

ArticleBuddyDemo.argTypes = {
  showDisclaimer: { control: { type: 'boolean' } },
  showActions: { control: { type: 'boolean' } },
  showReferences: { control: { type: 'boolean' } },
  showError: { control: { type: 'boolean' } },
  showFeedback: { control: { type: 'boolean' } }
};
